import { v3 } from "backoffice-api"
import { Button } from "materia/src/components/Button/Button"
import { Icon } from "materia/src/components/Icon/Icon"
import { IconButton } from "materia/src/components/IconButton/IconButton"
import { Modal } from "materia/src/components/Modal/Modal"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { modifiers } from "ui-tools"
import { FiestaImage } from "utility-components"
import { megabytesToBytes } from "../../../tools/megabytesToBytes"
import s from "./ProductModalActivityAttachments.module.scss"

const MAX_FILE_SIZE_MB = 20

type ProductModalActivityAttachmentsProps = {
  productId: number
  activityItemId: string
  taskTitle: string
  isRejected: boolean
  taskNote: string | null
  taskPhotoUrl: string | null
  isLocked: boolean
  onBack: () => void
}
export const ProductModalActivityAttachments = ({
  productId,
  activityItemId,
  taskTitle,
  isRejected,
  taskNote,
  taskPhotoUrl,
  isLocked,
  onBack,
}: ProductModalActivityAttachmentsProps) => {
  const { t } = useTranslation()

  const [attachmentPhotoFile, setAttachmentPhotoFile] = useState<File | null>(
    null
  )
  const [attachmentNote, setAttachmentNote] = useState<string>(taskNote ?? "")
  const [isSaving, setIsSaving] = useState(false)
  const [shouldRemovePhoto, setShouldRemovePhoto] = useState(false)
  const [isPhotoModalOpen, setIsPhotoModalOpen] = useState(false)
  const [fileSizeError, setFileSizeError] = useState(false)

  const MAX_FILE_SIZE_BYTES = megabytesToBytes(MAX_FILE_SIZE_MB)

  const togglePhotoModal = () => {
    setIsPhotoModalOpen(!isPhotoModalOpen)
  }

  const handleAttachmentPhotoFileChange = async (file: File | null) => {
    if (file) {
      if (file.size > MAX_FILE_SIZE_BYTES) {
        setFileSizeError(true)
        return
      }
      setFileSizeError(false)
      setAttachmentPhotoFile(file)
      setShouldRemovePhoto(false)
    } else {
      setAttachmentPhotoFile(null)
    }
  }

  const handleAttachmentNoteChange = (note: string) => {
    setAttachmentNote(note)
  }

  const handleSaveAttachments = async () => {
    setIsSaving(true)
    try {
      const hasPhotoChanged = attachmentPhotoFile !== null || shouldRemovePhoto

      await v3.updateActivityCompletion({
        product_id: productId,
        activity_item_id: activityItemId,
        note: attachmentNote,
        ...(hasPhotoChanged && {
          photo: shouldRemovePhoto ? null : attachmentPhotoFile,
        }),
      })
      v3.getActivityItems.invalidateQueries([productId])
      onBack()
    } catch (error) {
      console.error("Failed to save attachments:", error)
    } finally {
      setIsSaving(false)
    }
  }

  const photoURL = attachmentPhotoFile
    ? URL.createObjectURL(attachmentPhotoFile)
    : taskPhotoUrl

  const hasPhoto = Boolean(
    attachmentPhotoFile || (taskPhotoUrl && !shouldRemovePhoto)
  )

  const removeAttachmentPhotoFile = () => {
    setAttachmentPhotoFile(null)
    setShouldRemovePhoto(true)
  }

  return (
    <>
      <div className={modifiers(s, "attachments", { isRejected })}>
        <div className={s.content}>
          <div className={s.task}>
            <div className={modifiers(s, "task__label", { isRejected })}>
              {isRejected
                ? t("activityAttachments.NEEDS_REVIEW")
                : t("activityAttachments.ADD_ATTACHMENTS")}
            </div>
            <h3 className={s.task__title}>{taskTitle}</h3>
          </div>

          {hasPhoto ? (
            <PreviewPhoto
              photoURL={photoURL}
              photoFileName={
                attachmentPhotoFile?.name ?? t("activityAttachments.PHOTO")
              }
              isLocked={isLocked}
              removeAttachmentPhotoFile={removeAttachmentPhotoFile}
              isPhotoModalOpen={isPhotoModalOpen}
              togglePhotoModal={togglePhotoModal}
              isLocalFile={Boolean(attachmentPhotoFile)}
            />
          ) : (
            <UploadPhoto
              handleAttachmentPhotoFileChange={handleAttachmentPhotoFileChange}
              isLocked={isLocked}
              fileSizeError={fileSizeError}
              maxFileSizeMb={MAX_FILE_SIZE_MB}
            />
          )}

          <TaskComment
            attachmentNote={attachmentNote}
            handleAttachmentNoteChange={handleAttachmentNoteChange}
            isLocked={isLocked}
          />
        </div>

        <div className={s.footer}>
          <button onClick={onBack} className={s.footer__cancel}>
            {t("activityAttachments.CANCEL")}
          </button>
          <Button
            size="big"
            theme="blue"
            borderRadius="32"
            onClick={handleSaveAttachments}
            disabled={isSaving || isLocked}
          >
            {isSaving
              ? t("activityAttachments.SAVING")
              : t("activityAttachments.SAVE_ATTACHMENTS")}
          </Button>
        </div>
      </div>
    </>
  )
}

type PreviewPhotoProps = {
  photoURL: string | null
  photoFileName: string
  isLocked: boolean
  isPhotoModalOpen: boolean
  togglePhotoModal: () => void
  removeAttachmentPhotoFile: () => void
  isLocalFile: boolean
}

const PreviewPhoto = ({
  photoURL,
  photoFileName,
  isLocked,
  isPhotoModalOpen,
  togglePhotoModal,
  removeAttachmentPhotoFile,
  isLocalFile,
}: PreviewPhotoProps) => {
  const { t } = useTranslation()
  if (!photoURL) return null
  return (
    <div className={s.photo__preview}>
      <div className={s.photo__previewLeft}>
        <button
          aria-label={t("activityAttachments.VIEW_PHOTO")}
          title={t("activityAttachments.VIEW_PHOTO")}
          onClick={togglePhotoModal}
        >
          {isLocalFile ? (
            <img className={s.photo__previewImage} src={photoURL} alt="" />
          ) : (
            <FiestaImage
              className={s.photo__previewImage}
              src={photoURL}
              alt=""
              sizes="48px"
            />
          )}
        </button>
        <div className={s.photo__previewFileName}>{photoFileName}</div>
      </div>

      {!isLocked && (
        <IconButton
          as="button"
          icon="Close"
          size="small"
          aria-label={t("activityAttachments.REMOVE_PHOTO") as string}
          title={t("activityAttachments.REMOVE_PHOTO") as string}
          onClick={removeAttachmentPhotoFile}
        />
      )}

      {isPhotoModalOpen && (
        <Modal
          ariaLabel={t("activityAttachments.VIEW_PHOTO")}
          onClose={togglePhotoModal}
          showCloseButton
        >
          {isLocalFile ? (
            <img src={photoURL} className={s.photo__previewPhotoModal} alt="" />
          ) : (
            <FiestaImage
              className={s.photo__previewPhotoModal}
              src={photoURL}
              alt=""
              sizes="500px"
            />
          )}
        </Modal>
      )}
    </div>
  )
}

type UploadPhotoProps = {
  handleAttachmentPhotoFileChange: (file: File | null) => void
  isLocked: boolean
  fileSizeError: boolean
  maxFileSizeMb: number
}

const UploadPhoto = ({
  handleAttachmentPhotoFileChange,
  isLocked,
  fileSizeError,
  maxFileSizeMb,
}: UploadPhotoProps) => {
  const { t } = useTranslation()
  return (
    <label
      htmlFor="upload-activity-attachment-photo"
      className={modifiers(s, "photo__upload", { isLocked })}
    >
      <input
        type="file"
        disabled={isLocked}
        name="upload-activity-attachment-photo"
        id="upload-activity-attachment-photo"
        accept="image/jpeg,image/png,image/webp,image/avif"
        className={s.photo__uploadInput}
        onChange={(e) =>
          handleAttachmentPhotoFileChange(
            e.target.files && e.target.files[0] ? e.target.files[0] : null
          )
        }
      />
      <div className={s.photo__uploadButton}>
        <Icon icon="Plus" />
      </div>
      <div className={s.photo__uploadText}>
        <div className={s.photo__uploadTextTitle}>
          {t("activityAttachments.ADD_IMAGE")}
        </div>
        <div className={s.photo__uploadTextSubtitle}>
          {fileSizeError
            ? t("activityAttachments.FILE_TOO_LARGE", { size: maxFileSizeMb })
            : t("activityAttachments.MAX_FILE_SIZE", { size: maxFileSizeMb })}
        </div>
      </div>
    </label>
  )
}

type TaskCommentProps = {
  attachmentNote: string
  handleAttachmentNoteChange: (note: string) => void
  isLocked: boolean
}

const TaskComment = ({
  attachmentNote,
  isLocked,
  handleAttachmentNoteChange,
}: TaskCommentProps) => {
  const { t } = useTranslation()
  return (
    <label className={s.textarea}>
      <div className={s.textarea__label}>
        {t("activityAttachments.TASK_COMMENT")}
      </div>
      <textarea
        placeholder={t("activityAttachments.TASK_COMMENT_PLACEHOLDER")}
        className={s.textarea__input}
        value={attachmentNote}
        disabled={isLocked}
        onChange={(e) => handleAttachmentNoteChange(e.target.value)}
      />
    </label>
  )
}
